// Pure-math helpers for SVG arc paths. Extracted so the Chart renderers
// stay short and the math is unit-testable in isolation.

export interface ArcDims {
  cx: number;
  cy: number;
  outerR: number;
  innerR: number;
}

export function pointOnCircle(
  cx: number,
  cy: number,
  r: number,
  angle: number,
): [number, number] {
  // Angles run clockwise from 12 o'clock (subtract π/2).
  return [
    cx + Math.cos(angle - Math.PI / 2) * r,
    cy + Math.sin(angle - Math.PI / 2) * r,
  ];
}

export function arcPath(
  dims: ArcDims,
  startAngle: number,
  endAngle: number,
): string {
  const { cx, cy, outerR, innerR } = dims;
  const isFull = endAngle - startAngle >= Math.PI * 2 - 1e-6;
  if (isFull) {
    return [
      `M ${cx + outerR} ${cy}`,
      `A ${outerR} ${outerR} 0 1 1 ${cx - outerR} ${cy}`,
      `A ${outerR} ${outerR} 0 1 1 ${cx + outerR} ${cy}`,
      innerR > 0 ? `M ${cx + innerR} ${cy}` : "",
      innerR > 0 ? `A ${innerR} ${innerR} 0 1 0 ${cx - innerR} ${cy}` : "",
      innerR > 0 ? `A ${innerR} ${innerR} 0 1 0 ${cx + innerR} ${cy}` : "",
      "Z",
    ].filter(Boolean).join(" ");
  }
  const [x1, y1] = pointOnCircle(cx, cy, outerR, startAngle);
  const [x2, y2] = pointOnCircle(cx, cy, outerR, endAngle);
  const large = endAngle - startAngle > Math.PI ? 1 : 0;
  if (innerR <= 0) {
    // Pie wedge — outer arc + two radii.
    return [
      `M ${cx} ${cy}`,
      `L ${x1} ${y1}`,
      `A ${outerR} ${outerR} 0 ${large} 1 ${x2} ${y2}`,
      "Z",
    ].join(" ");
  }
  const [xi1, yi1] = pointOnCircle(cx, cy, innerR, endAngle);
  const [xi2, yi2] = pointOnCircle(cx, cy, innerR, startAngle);
  return [
    `M ${x1} ${y1}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${x2} ${y2}`,
    `L ${xi1} ${yi1}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${xi2} ${yi2}`,
    "Z",
  ].join(" ");
}
